import { config } from "@nova/configuration/browser"
import { useRouter } from "next/router"
import { normalize } from "@nova/util-strings"
import { extractUtmParams } from "user/registration/module"
import { appleAuthUrl, decodeBase64UrlSafeToJson, microsoftAuthUrl } from "./module"
import { useAppDispatch } from "integrations/redux/hooks"
import { verifyAppleLogin, verifyMicrosoftLogin } from "user/authentication/thunks"
import { useEffectUnsafe } from "@nova/use-effect-unsafe"
import { LoginFormSeparator } from "../LoginFormSeparator"
import { ButtonFederated } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"

type FederatedGroupProps = {
    setIsVerifyingSso: (isVerifyingSso: boolean) => void
}

type StatePayload = {
    redirect?: string
    utm?: Record<string, string>
}

export function FederatedGroup({ setIsVerifyingSso }: FederatedGroupProps) {
    const dispatch = useAppDispatch()

    const router = useRouter()
    const { redirect } = router.query
    const utmParams = extractUtmParams()
    const environmentName = config.get("environmentName")

    const baseUrl = `sso.${environmentName === "production" ? "app" : environmentName}.mapnova.com`

    // Create state parameter with both redirect URL and UTM params for conversion tracking
    // Per RFC 6749 Section 4.1.1: state parameter is used "to maintain state between the request and callback"
    // https://datatracker.ietf.org/doc/html/rfc6749#section-4.1.1
    const createStateParam = (): string => {
        const normalizedRedirect = normalize(redirect)
        if (Object.keys(utmParams).length === 0) {
            return encodeURIComponent(normalizedRedirect)
        }
        const statePayload: StatePayload = {
            redirect: normalizedRedirect,
            utm: utmParams,
        }
        return encodeURIComponent(JSON.stringify(statePayload))
    }

    /**
     * Handles OAuth callbacks by parsing respective query params and dispatching
     * verification requests.
     *
     * Unsafe because we only want to run this side effect when the router query
     * params change.
     */
    useEffectUnsafe(() => {
        const handleQueryParamsChanged = async () => {
            // Error Handling
            // const errorParam = router.query["error"]
            // if (errorParam && typeof errorParam === "string") {
            //     const error = decodeBase64UrlSafeToJson(errorParam) as {
            //         message: string
            //         details: string
            //     }
            //     // TODO: set error state @Jarren
            //     console.error(error)
            // }

            // Apple
            const appleCallbackParam = router.query["apple-callback"]
            if (appleCallbackParam && typeof appleCallbackParam === "string") {
                setIsVerifyingSso(true)
                dispatch(verifyAppleLogin({ appleCallbackParam }))

                // Clear Microsoft params from URL.
                const query = { ...router.query }
                delete query["apple-callback"]
                router.replace(
                    {
                        pathname: router.pathname,
                        query,
                    },
                    undefined,
                    { shallow: true }
                )

                return
            }

            // Microsoft
            const stateParam = router.query["state"]
            const codeParam = router.query["code"]
            if (typeof stateParam === "string" && typeof codeParam === "string") {
                const { provider } = decodeBase64UrlSafeToJson(stateParam) as {
                    provider: string
                }
                if (provider !== "Microsoft") {
                    console.error("Unknown auth provider:", provider)
                    return
                }

                setIsVerifyingSso(true)
                dispatch(verifyMicrosoftLogin({ microsoftCallbackCode: codeParam }))

                // Clear Microsoft params from URL.
                const query = { ...router.query }
                delete query.code
                delete query.state
                delete query.session_state
                router.replace(
                    {
                        pathname: router.pathname,
                        query,
                    },
                    undefined,
                    { shallow: true }
                )

                return
            }
        }

        handleQueryParamsChanged()
    }, [router])

    return (
        <div className="w-full items-center">
            <LoginFormSeparator title="Continue with" />
            <div className="my-4 flex gap-4 justify-center">
                <a
                    className="text-black no-underline"
                    href={`https://${baseUrl}/oauth2/authorize?response_type=code&identity_provider=Google&client_id=${config.get(
                        "awsUserPoolWebClientId"
                    )}&idp_identifier=Google&state=${createStateParam()}&redirect_uri=${window.location.origin}/api/sso-redirect`}
                >
                    <ButtonFederated Icon={Icons.Google} />
                </a>

                <a className="no-underline" href={microsoftAuthUrl()} target="_self">
                    <ButtonFederated Icon={Icons.Microsoft} />
                </a>

                <a
                    className="text-black no-underline"
                    href={appleAuthUrl(createStateParam())}
                    target="_self"
                >
                    <ButtonFederated Icon={Icons.Apple} />
                </a>
            </div>
            <LoginFormSeparator title="or" />
        </div>
    )
}
